import { useT } from "@/context/LanguageContext";
import { translations } from "@/data/translations";
import { motion } from "framer-motion";
import PhoneMockup from "./PhoneMockup";

export default function Hero() {
  const t = useT(translations.hero);

  return (
    <section
      id="hero"
      data-testid="hero-section"
      className="relative min-h-screen flex items-center pt-16 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-[#1a1f26]">
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage:
              "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E\")",
          }}
        />
        <div
          className="absolute inset-0 opacity-100"
          style={{
            backgroundImage:
              "linear-gradient(to right, #80808008 1px, transparent 1px), linear-gradient(to bottom, #80808008 1px, transparent 1px)",
            backgroundSize: "48px 48px",
          }}
        />
        {/* Copper glow */}
        <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-[#b87b56]/5 rounded-full blur-[120px]" />
      </div>

      <div className="container mx-auto px-4 md:px-6 lg:px-8 max-w-7xl relative z-10">
        <div className="flex flex-col lg:flex-row items-center justify-between gap-12 lg:gap-16">
          {/* Left content */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex-1 text-center lg:text-left"
          >
            <span className="inline-block text-sm font-medium tracking-[0.3em] uppercase text-[#b87b56] mb-4">
              {t.tagline}
            </span>

            <h1
              className="text-5xl md:text-7xl font-bold tracking-tight uppercase text-white leading-[0.95] mb-6"
              style={{ fontFamily: "Oswald, sans-serif" }}
            >
              {t.title.split(" ").map((word, i) => (
                <span key={i}>
                  {word === "ARTS" || word === "TIMER" ? (
                    <span className="text-[#b87b56]">{word}</span>
                  ) : (
                    word
                  )}{" "}
                </span>
              ))}
            </h1>

            <p className="text-base md:text-lg leading-relaxed text-slate-300 max-w-xl mb-10 mx-auto lg:mx-0">
              {t.subtitle}
            </p>

            {/* Store buttons */}
            <div className="flex flex-col sm:flex-row items-center gap-4 justify-center lg:justify-start">
              <a
                href="https://apps.apple.com/us/app/boxing-mma-timer/id6760870249"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="app-store-btn"
                className="h-14 px-6 rounded-md bg-black border border-white/10 hover:border-white/30 flex items-center gap-3 transition-all w-full sm:w-auto justify-center"
              >
                <svg viewBox="0 0 24 24" className="w-7 h-7 text-white fill-current">
                  <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z" />
                </svg>
                <div className="text-left">
                  <div className="text-[10px] text-slate-400 uppercase tracking-wider leading-none">
                    {t.downloadOn}
                  </div>
                  <div className="text-white font-semibold text-sm leading-tight">
                    {t.appStore}
                  </div>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Right - Phone mockup */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex-shrink-0"
          >
            <PhoneMockup variant="timer" />
          </motion.div>
        </div>
      </div>
    </section>
  );
}
